import { FilterGroup } from '../page-component/PageTypes';

/**
 * Builds a lookup from filter ID to its resolved contextKey. A per-filter
 * `contextKey` overrides the enclosing group's — useful when a filter lives
 * visually in one panel but needs to share storage with another surface.
 * Filters without any contextKey are mapped to undefined (written flat to root).
 */
export function buildContextKeyMap(
  filterGroups: FilterGroup[]
): Record<string, string | undefined> {
  const map: Record<string, string | undefined> = {};
  for (const group of filterGroups) {
    for (const filter of group.data) {
      const resolved = filter.contextKey ?? group.contextKey;
      map[filter.id] = resolved;
      // Also map date-range start/end keys
      if (filter.start) map[filter.start] = resolved;
      if (filter.end) map[filter.end] = resolved;
      // Map conditional render filter IDs
      if (filter.conditionalRender?.id) {
        map[filter.conditionalRender.id] = resolved;
      }
    }
  }
  return map;
}

/**
 * Groups flat filter entries into the context shape based on contextKey mapping.
 * Keys with a contextKey are nested: { [contextKey]: { key: value } }
 * Keys without a contextKey are written flat to the root.
 */
export function groupFiltersForContext(
  flatFilters: Record<string, unknown>,
  contextKeyMap: Record<string, string | undefined>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(flatFilters)) {
    const contextKey = contextKeyMap[key];
    if (contextKey) {
      if (!result[contextKey]) result[contextKey] = {};
      (result[contextKey] as Record<string, unknown>)[key] = value;
    } else {
      result[key] = value;
    }
  }

  return result;
}

/**
 * Flattens grouped context filters back into a flat object for form consumption.
 * Uses the contextKey map to identify which top-level keys are groups to expand.
 * { taskFilters: { a: 1 }, resourceFilters: { b: 2 }, c: 3 } → { a: 1, b: 2, c: 3 }
 */
export function flattenContextFilters(
  filters: Record<string, unknown> | undefined,
  filterGroups: FilterGroup[]
): Record<string, unknown> {
  if (!filters || typeof filters !== 'object') return {};

  const contextKeys = new Set(
    filterGroups.map((g) => g.contextKey).filter(Boolean)
  );
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filters)) {
    if (
      contextKeys.has(key) &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      Object.assign(result, value);
    } else {
      result[key] = value;
    }
  }

  return result;
}
